import { useCallback } from "react";
import { useFileStorageValidator } from "./useFileStorageValidator";
import { Base64File } from "../types";

/**
 * Hook for encoding files to base64 format.
 * Validates files before encoding and detects MIME type.
 */
export const useBase64FileEncoder = () => {
  const { validateFiles } = useFileStorageValidator();

  const encode = useCallback(
    async (file: File): Promise<Base64File> => {
      const validationError = await validateFiles([file]);
      if (validationError) {
        throw new Error(validationError);
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const value = typeof reader.result === "string" ? reader.result : "";
          resolve(value);
        };
        reader.onerror = () => reject(new Error("Unable to read file."));
        reader.readAsDataURL(file);
      });

      const [, base64Content = ""] = base64.split(",");

      return {
        filename: file.name,
        content: base64Content,
        mimeType: file.type || "application/octet-stream",
      };
    },
    [validateFiles],
  );

  return { encode };
};
